import { z } from 'zod';
import { STATUS_IMPORTAVEIS } from './alunos.js';
import { zId } from './primitivos.js';
/**
 * Os contratos da importação de planilha — `#m-importar` e a tela de
 * conferência que o protótipo não tem.
 *
 * Duas fases separadas, e a separação é a entrega inteira:
 *
 *   ANÁLISE    arquivo → leitura → de-para → validação → relatório
 *              (nenhum aluno é gravado; a análise é persistida)
 *   GRAVAÇÃO   relatório confirmado → job por lote → alunos criados
 *
 * O vocabulário mora aqui porque os dois lados o falam: o servidor
 * diagnostica, a tela filtra e rotula pelos MESMOS códigos.
 *
 * Ver: Arena_backend/Docs/plano/09-importacao-csv.md
 */
// ---------------------------------------------------------------------
// Limites — recusar com mensagem clara em vez de travar o worker
// ---------------------------------------------------------------------
/**
 * 2 MB. Uma planilha de quinhentos alunos tem uns 70 KB; o teto deixa
 * folga de trinta vezes e ainda recusa, na porta, o arquivo errado — a
 * exportação do sistema anterior com todas as tabelas juntas.
 */
export const TAMANHO_MAXIMO_DO_CSV = 2 * 1024 * 1024;
/** Linhas de dados por importação. Acima disso, dividir a planilha em partes. */
export const MAXIMO_DE_LINHAS_DO_CSV = 5_000;
// ---------------------------------------------------------------------
// De-para — as colunas da planilha e os campos do cadastro
// ---------------------------------------------------------------------
/**
 * Os campos de destino: os obrigatórios do documento 08 mais os
 * opcionais que uma planilha costuma ter.
 *
 * Fora, de propósito: `origem` (é sempre "Importação de planilha"),
 * `observacoesMedicas` (dado de saúde exige a projeção completa, e a
 * planilha é justamente por onde ele se espalha sem controle) e os dois
 * consentimentos (ato do titular — não se infere de planilha).
 */
export const CAMPOS_DA_IMPORTACAO = [
    'nome',
    'telefone',
    'nascimento',
    'status',
    'apelido',
    'cpf',
    'email',
    'desde',
    'contatoEmergenciaNome',
    'contatoEmergenciaTelefone',
    'responsavelFinanceiro',
];
/**
 * Sem coluna para um destes, nenhuma linha passa — a tela bloqueia a
 * confirmação e diz qual falta. `status` não está aqui: sem coluna, vale
 * o status escolhido na tela.
 */
export const CAMPOS_OBRIGATORIOS_DA_IMPORTACAO = ['nome', 'telefone', 'nascimento'];
export const ROTULO_CAMPO_DA_IMPORTACAO = {
    nome: 'Nome',
    telefone: 'Telefone',
    nascimento: 'Nascimento',
    status: 'Situação',
    apelido: 'Apelido',
    cpf: 'CPF',
    email: 'E-mail',
    desde: 'Aluno desde',
    contatoEmergenciaNome: 'Contato de emergência',
    contatoEmergenciaTelefone: 'Telefone de emergência',
    responsavelFinanceiro: 'Responsável financeiro',
};
/**
 * O mapeamento: uma posição por coluna da planilha, com o campo que ela
 * alimenta ou `null` (coluna ignorada). Dois campos iguais em colunas
 * diferentes é recusado — qual das duas valeria seria decidido pela
 * ordem, e ninguém lê a ordem.
 */
export const zMapeamento = z
    .array(z.enum(CAMPOS_DA_IMPORTACAO).nullable())
    .min(1, 'A planilha precisa de ao menos uma coluna.')
    .max(200, 'Colunas demais.')
    .superRefine((campos, ctx) => {
    const vistos = new Map();
    campos.forEach((campo, indice) => {
        if (!campo)
            return;
        const anterior = vistos.get(campo);
        if (anterior !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: [indice],
                message: `"${ROTULO_CAMPO_DA_IMPORTACAO[campo]}" já está na coluna ${anterior + 1}.`,
            });
            return;
        }
        vistos.set(campo, indice);
    });
});
export const ORIGENS_DO_MAPEAMENTO = ['automatico', 'importacao_anterior', 'manual'];
// ---------------------------------------------------------------------
// O arquivo — separador e codificação
// ---------------------------------------------------------------------
/**
 * Por nome, e não pelo caractere: a tabulação não viaja bem em query
 * string nem em CHECK de banco, e "ponto_e_virgula" se lê no log.
 */
export const SEPARADORES = ['ponto_e_virgula', 'virgula', 'tabulacao', 'barra'];
export const CARACTERE_DO_SEPARADOR = {
    ponto_e_virgula: ';',
    virgula: ',',
    tabulacao: '\t',
    barra: '|',
};
export const ROTULO_SEPARADOR = {
    ponto_e_virgula: 'Ponto e vírgula',
    virgula: 'Vírgula',
    tabulacao: 'Tabulação',
    barra: 'Barra vertical',
};
/**
 * `windows-1252` e não `iso-8859-1`: é o que o Excel brasileiro grava de
 * fato, e o nome que o WHATWG dá ao rótulo "latin1". Os dois coincidem
 * em todas as letras acentuadas do português; o 1252 ainda cobre as
 * aspas curvas e o travessão que o Word cola na célula.
 */
export const CODIFICACOES = ['utf-8', 'windows-1252', 'utf-16le', 'utf-16be'];
export const ROTULO_CODIFICACAO = {
    'utf-8': 'UTF-8',
    'windows-1252': 'ISO-8859-1 (Excel)',
    'utf-16le': 'UTF-16',
    'utf-16be': 'UTF-16 (big-endian)',
};
// ---------------------------------------------------------------------
// Diagnóstico por linha
// ---------------------------------------------------------------------
/**
 * A tabela do documento 09, com dois códigos a mais: CPF repetido no
 * arquivo e CPF já cadastrado. O telefone continua sendo a chave de
 * duplicidade — é o que a planilha tem preenchido —, mas quando o CPF
 * VEM, ele é a identificação mais forte que existe: irmãos dividem
 * telefone, não dividem CPF. Ignorá-lo deixaria entrar em dobro a
 * pessoa que só trocou de número.
 *
 *   ok                               será criado
 *   normalizado                      aceito com ajuste — informativo
 *   telefone_duplicado_no_arquivo    outra linha do arquivo tem o mesmo telefone
 *   telefone_existente               já há aluno com esse telefone na arena
 *   cpf_duplicado_no_arquivo         outra linha do arquivo tem o mesmo CPF
 *   cpf_existente                    já há aluno com esse CPF na arena
 *   campo_obrigatorio_ausente        bloqueia a linha
 *   formato_invalido                 bloqueia a linha
 */
export const DIAGNOSTICOS = [
    'ok',
    'normalizado',
    'telefone_duplicado_no_arquivo',
    'telefone_existente',
    'cpf_duplicado_no_arquivo',
    'cpf_existente',
    'campo_obrigatorio_ausente',
    'formato_invalido',
];
export const DIAGNOSTICOS_QUE_BLOQUEIAM = [
    'campo_obrigatorio_ausente',
    'formato_invalido',
];
export const DIAGNOSTICOS_DE_DUPLICIDADE = [
    'telefone_duplicado_no_arquivo',
    'telefone_existente',
    'cpf_duplicado_no_arquivo',
    'cpf_existente',
];
/** Duplicidade contra a BASE — as únicas que admitem "atualizar o existente". */
export const DIAGNOSTICOS_DA_BASE = ['telefone_existente', 'cpf_existente'];
export const ROTULO_DIAGNOSTICO = {
    ok: 'Será criado',
    normalizado: 'Aceito com ajuste',
    telefone_duplicado_no_arquivo: 'Telefone repetido no arquivo',
    telefone_existente: 'Telefone já cadastrado',
    cpf_duplicado_no_arquivo: 'CPF repetido no arquivo',
    cpf_existente: 'CPF já cadastrado',
    campo_obrigatorio_ausente: 'Campo obrigatório ausente',
    formato_invalido: 'Formato inválido',
};
/**
 * A categoria da linha — o que os contadores do topo somam. Mutuamente
 * exclusivas: bloqueio vence duplicidade, que vence o resto. "214
 * válidas, 6 duplicadas" é 220 linhas, não 214.
 */
export const SITUACOES_DA_LINHA = ['valida', 'duplicada', 'bloqueada'];
export function situacaoDe(ocorrencias) {
    if (ocorrencias.some((o) => DIAGNOSTICOS_QUE_BLOQUEIAM.includes(o.codigo)))
        return 'bloqueada';
    if (ocorrencias.some((o) => DIAGNOSTICOS_DE_DUPLICIDADE.includes(o.codigo)))
        return 'duplicada';
    return 'valida';
}
// ---------------------------------------------------------------------
// Decisão por linha
// ---------------------------------------------------------------------
/**
 * "Duplicidade não bloqueia por padrão — a linha é marcada e o operador
 * escolhe entre pular ou atualizar o cadastro existente." A escolha vale
 * por linha.
 *
 * `criar` também é escolha possível na linha duplicada, e não só no
 * padrão da válida: irmãos e casais dividem telefone — o documento 08
 * aceita o segundo cadastro no formulário pelo mesmo motivo. Nunca é o
 * padrão da duplicada.
 */
export const DECISOES = ['criar', 'atualizar', 'pular'];
export const ROTULO_DECISAO = {
    criar: 'Criar cadastro',
    atualizar: 'Atualizar o existente',
    pular: 'Pular',
};
export function decisaoPadrao(situacao) {
    return situacao === 'valida' ? 'criar' : 'pular';
}
/**
 * O que a linha admite. Bloqueada só se pula; atualizar exige um
 * cadastro na base para atualizar.
 */
export function decisoesPossiveis(situacao, temCandidatoNaBase) {
    if (situacao === 'bloqueada')
        return ['pular'];
    if (situacao === 'valida')
        return ['criar', 'pular'];
    return temCandidatoNaBase ? ['pular', 'atualizar', 'criar'] : ['pular', 'criar'];
}
export const RESULTADOS_DA_LINHA = ['criado', 'atualizado', 'pulado', 'falha'];
// ---------------------------------------------------------------------
// O ciclo da importação
// ---------------------------------------------------------------------
/**
 *   analisada    o relatório existe; decisões e de-para ainda mudam
 *   gravando     confirmada; o job está criando os alunos, lote a lote
 *   concluida    toda linha tem resultado
 *   descartada   desistiu antes de gravar
 */
export const ETAPAS_DA_IMPORTACAO = ['analisada', 'gravando', 'concluida', 'descartada'];
export const ROTULO_ETAPA_DA_IMPORTACAO = {
    analisada: 'Aguardando confirmação',
    gravando: 'Gravando',
    concluida: 'Concluída',
    descartada: 'Descartada',
};
// ---------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------
/**
 * A query de `POST /importacoes` — o corpo é o arquivo, cru.
 *
 * `automatico` é o padrão das duas escolhas de `#m-importar`: a
 * detecção acerta a planilha comum, e a escolha manual existe para a
 * que ela erra.
 */
export const zOpcoesDaAnalise = z.object({
    nome: z
        .string({ required_error: 'Informe o nome do arquivo.' })
        .trim()
        .min(1, 'Informe o nome do arquivo.')
        .max(200, 'Nome de arquivo longo demais.'),
    separador: z.enum(['automatico', ...SEPARADORES]).default('automatico'),
    codificacao: z.enum(['automatica', ...CODIFICACOES]).default('automatica'),
    status: z.enum(STATUS_IMPORTAVEIS).default('Ativo'),
});
/** `PATCH /importacoes/:id` — corrigir o de-para ou o status da tela, antes de confirmar. */
export const zRevisaoDaImportacao = z
    .object({
    mapeamento: zMapeamento.optional(),
    statusPadrao: z.enum(STATUS_IMPORTAVEIS).optional(),
})
    .refine((r) => r.mapeamento !== undefined || r.statusPadrao !== undefined, {
    message: 'Nada para alterar.',
});
/**
 * `PUT /importacoes/:id/decisoes` — por linha. "Pular todas as
 * duplicadas" é a tela mandando a lista inteira: a regra continua sendo
 * a da linha.
 */
export const zDecisoesDaImportacao = z.object({
    decisoes: z
        .array(z.object({
        linha: z.number().int().min(2),
        decisao: z.enum(DECISOES),
        /** Qual cadastro atualizar, quando a linha tem mais de um candidato. */
        alunoAlvoId: zId.optional(),
    }))
        .min(1, 'Nenhuma decisão enviada.')
        .max(MAXIMO_DE_LINHAS_DO_CSV),
});
/**
 * `GET /importacoes/:id/linhas` — o relatório, filtrado por diagnóstico.
 * `ok` filtra as linhas que serão criadas. O cursor é o número da última
 * linha da página: a ordem é a do arquivo.
 */
export const zConsultaDeLinhas = z.object({
    diagnostico: z.enum(DIAGNOSTICOS).optional(),
    apos: z.coerce.number().int().min(1).optional(),
    limite: z.coerce.number().int().min(1).max(500).default(100),
});
export const AVISOS_DA_IMPORTACAO = [
    'codificacao_convertida',
    'acentuacao_corrompida',
    'aspas_sem_fechamento',
    'arquivo_ja_importado',
    'mapeamento_reaproveitado',
    'colunas_sem_titulo',
    'base_mudou',
    'colunas_a_mais',
];
//# sourceMappingURL=importacao.js.map